<template>
  <v-card>
    <v-toolbar color="purple" dark dense flat>
      <v-toolbar-title>{{ isEditing ? 'Editar Iniciativa' : 'Nova Iniciativa' }}</v-toolbar-title>
      <v-spacer></v-spacer>
      <v-btn icon @click="$emit('close')"><v-icon>mdi-close</v-icon></v-btn>
    </v-toolbar>
    <v-card-text class="pt-4" style="max-height: 80vh; overflow-y: auto;">
      <v-form ref="form" v-model="valid">   
       <!-- Descrição da Iniciativa -->
        <v-row>
          <v-col cols="12">
            <v-textarea
              v-model="form.goal"
              label="Descrição da Iniciativa"
              outlined
              rows="3"
              hide-details="auto"
            ></v-textarea>
          </v-col>
        </v-row>
        <!-- Fatores SWOT -->
        <v-row class="mb-2">
          <v-col cols="12">
            <div class="text-subtitle-2 font-weight-bold mb-3">Fatores SWOT</div>

            <div class="d-flex align-center" style="gap: 8px;">
              <v-select
                v-model="currentFactorType"
                :items="typeOptions"
                label="Tipo de Fator"
                outlined
                dense
                hide-details="auto"
                style="max-width: 200px; min-width: 160px; flex: 0 0 auto;"
                @change="currentFactorValues = []"
              ></v-select>

              <v-select
                v-model="currentFactorValues"
                :items="getFactorOptions(currentFactorType)"
                item-text="displayDescription"
                item-value="id"
                :label="currentFactorType ? 'Fatores - ' + currentFactorType : 'Fatores'"
                :disabled="!currentFactorType"
                multiple
                outlined
                dense
                hide-details="auto"
                style="flex: 1 1 0; min-width: 0;"
                @change="onFactorValuesChange"
              >
                <template v-slot:item="{ item, on, attrs }">
                  <v-list-item v-bind="attrs" v-on="on">
                    <v-list-item-action>
                      <v-checkbox :input-value="attrs.inputValue"></v-checkbox>
                    </v-list-item-action>
                    <v-list-item-content>
                      <div class="d-flex align-center">
                        <v-icon
                          v-if="item.perspectiveId"
                          :color="perspectiveColor(item.perspectiveId)"
                          small
                          class="mr-1"
                        >{{ perspectiveIcon(item.perspectiveId) }}</v-icon>
                        <v-tooltip right>
                          <template v-slot:activator="{ on: tip, attrs: tipAttrs }">
                            <v-list-item-title v-bind="tipAttrs" v-on="tip" class="text-truncate" style="max-width: 300px">
                              {{ item.displayDescription }}
                            </v-list-item-title>
                          </template>
                          <span>{{ item.displayDescription }}</span>
                        </v-tooltip>
                      </div>
                      <v-list-item-subtitle v-if="item.perspectiveId" class="text--secondary" style="font-size: 0.65rem; line-height: 1;">
                        {{ perspectiveName(item.perspectiveId) }}
                      </v-list-item-subtitle>
                    </v-list-item-content>
                  </v-list-item>
                </template>
              </v-select>
            </div>

            <!-- Lista de fatores confirmados -->
            <div v-if="confirmedFactors.length > 0" class="mt-3">
              <v-divider class="mb-2"></v-divider>
              <div class="caption grey--text text--darken-1 mb-1">Fatores selecionados:</div>
              <div class="d-flex flex-wrap" style="gap: 4px;">
                <div
                  v-for="(item, index) in confirmedFactors"
                  :key="index"
                  class="d-flex align-center mb-1"
                  style="gap: 2px;"
                >
                  <v-chip x-small :color="getTypeColor(item.type)" dark class="mr-1">{{ item.type }}</v-chip>
                  <v-tooltip v-if="item.perspectiveId" bottom>
                    <template v-slot:activator="{ on, attrs }">
                      <v-icon
                        :color="perspectiveColor(item.perspectiveId)"
                        small
                        class="mr-1"
                        v-bind="attrs"
                        v-on="on"
                      >{{ perspectiveIcon(item.perspectiveId) }}</v-icon>
                    </template>
                    <span>{{ perspectiveName(item.perspectiveId) }}</span>
                  </v-tooltip>
                  <v-tooltip bottom>
                    <template v-slot:activator="{ on, attrs }">
                      <v-chip x-small outlined class="mr-1" v-bind="attrs" v-on="on">{{ item.label }}</v-chip>
                    </template>
                    <span>{{ item.label }}</span>
                  </v-tooltip>
                  <v-btn icon x-small color="red lighten-1" @click="removeConfirmedFactor(index)">
                    <v-icon x-small>mdi-minus-circle</v-icon>
                  </v-btn>
                </div>
              </div>
            </div>
          </v-col>
        </v-row>
       <!-- Objetivo -->
        <v-row>
          <v-col cols="12">
            <v-select
              v-model="form.objectiveId"
              :items="objectiveOptions"
              item-text="label"
              item-value="id"
              label="Objetivos Estratégicos"
              outlined
              dense
              hide-details="auto"
            >
              <template v-slot:selection="{ item }">
                <v-icon
                  v-for="pid in item.perspectiveIds"
                  :key="pid"
                  :color="perspectiveColor(pid)"
                  small
                  class="mr-1"
                >{{ perspectiveIcon(pid) }}</v-icon>
                <span>{{ item.code }} - {{ item.description }}</span>
              </template>
              <template v-slot:item="{ item }">
                <v-list-item-content class="py-1">
                  <div class="d-flex align-center">
                    <v-icon
                      v-for="pid in item.perspectiveIds"
                      :key="pid"
                      :color="perspectiveColor(pid)"
                      small
                      class="mr-1"
                    >{{ perspectiveIcon(pid) }}</v-icon>
                    <span class="font-weight-medium">{{ item.code }}</span>
                  </div>
                  <div class="text-caption">{{ item.description }}</div>
                  <div class="text--secondary" style="font-size: 0.65rem; line-height: 1;">
                    {{ objectivePerspectiveNames(item) }}
                  </div>
                </v-list-item-content>
              </template>
            </v-select>
          </v-col>   
        </v-row>
        <!-- Método -->
        <v-row>
          <v-col cols="6" md="4">
            <v-select
              v-model="form.method"
              :items="methodOptions"
              label="Método"
              outlined
              dense
              hide-details="auto"
            ></v-select>
          </v-col>
          <v-col cols="6" md="4">
            <v-text-field
              v-model="targetValueText"
              label="Meta"
              type="text"
              inputmode="decimal"
              outlined
              dense
              :disabled="form.method !== 'Quantitativo'"
              :hide-details="!targetCommaWarning"
              :error="targetCommaWarning"
              :error-messages="targetCommaWarning ? 'Use ponto (.) para casas decimais' : ''"
              @keypress="blockInvalidDecimalKey($event, 'target')"
            ></v-text-field>
          </v-col>
          <v-col cols="6" md="4">
            <v-text-field
              v-model="resultValueText"
              label="Resultado"
              type="text"
              inputmode="decimal"
              outlined
              dense
              :disabled="form.method !== 'Quantitativo'"
              :hide-details="!resultCommaWarning"
              :error="resultCommaWarning"
              :error-messages="resultCommaWarning ? 'Use ponto (.) para casas decimais' : ''"
              @keypress="blockInvalidDecimalKey($event, 'result')"
            ></v-text-field>
          </v-col>
        </v-row>

        <!-- Percentual: qualitativo = planos finalizados, quantitativo = resultado / meta -->
        <v-row>
          <v-col cols="12">
            <div class="d-flex justify-space-between caption">
              <span>Progresso</span>
              <span>{{ completionPercentage }}%</span>
            </div>
            <v-progress-linear
              :value="completionPercentage"
              height="15"
              color="success"
              rounded
            ></v-progress-linear>
            <div class="caption grey--text mt-1" v-if="form.method === 'Quantitativo'">
              Resultado {{ form.resultValue != null ? form.resultValue : 0 }} de meta {{ form.targetValue != null ? form.targetValue : 0 }}
            </div>
            <div class="caption grey--text mt-1" v-else>
              {{ finishedPlansCount }} de {{ form.plans.length }} plano{{ form.plans.length === 1 ? '' : 's' }} de ação finalizado{{ finishedPlansCount === 1 ? '' : 's' }}
            </div>
          </v-col>
        </v-row>

        <v-divider class="my-4"></v-divider>
          <v-row>
            <v-col cols="12">
              <div class="d-flex align-center justify-space-between mb-3">
                  <div class="text-h6">Planos de Ação</div>
                  <v-btn color="primary" small @click="openPlanForm">
                      <v-icon left>mdi-plus</v-icon> Novo
                  </v-btn>
              </div>
            </v-col>
         </v-row>

        <!-- Estado vazio: nenhum plano cadastrado ainda -->
        <v-row v-if="form.plans.length === 0">
          <v-col cols="12">
            <div
              class="d-flex flex-column align-center justify-center text-center pa-6 grey--text text--darken-1"
              style="border: 1px dashed #bdbdbd; border-radius: 4px;"
            >
              <v-icon size="32" color="grey lighten-1" class="mb-2">mdi-clipboard-text-outline</v-icon>
              <span>Nenhum plano de ação cadastrado ainda.</span>
              <span class="caption">Clique em "Novo" para adicionar o primeiro plano.</span>
            </div>
          </v-col>
        </v-row>

        <!-- Lista de Planos Adicionados -->
        <v-expansion-panels v-if="form.plans.length > 0" class="mb-4" accordion multiple>
          <v-expansion-panel v-for="(plan, index) in form.plans" :key="index">
            <v-expansion-panel-header color="grey lighten-5">
              <div class="d-flex align-center">
                  <v-icon small class="mr-2" color="primary">mdi-check-circle-outline</v-icon>
                  <strong>{{ plan.what }}</strong>
                  <v-spacer></v-spacer>
                  <v-chip x-small :color="getStatusColor(plan.status)" dark class="mr-2">
                    {{ plan.status }}
                  </v-chip>
              </div>
            </v-expansion-panel-header>
            <v-expansion-panel-content class="pt-2">
               <v-row dense>
                   <v-col cols="12" md="6"><strong>O Quê:</strong> {{ plan.what }}</v-col>
                   <v-col cols="12" md="6"><strong>Como:</strong> {{ plan.how }}</v-col>
                   <v-col cols="6"><strong>Onde:</strong> {{ plan.where }}</v-col>
                   <v-col cols="6"><strong>Quem:</strong> {{ plan.who }}</v-col>
                   <v-col cols="6"><strong>Responsável:</strong> {{ plan.responsible }}</v-col>
                   <v-col cols="6"><strong>Custo:</strong> {{ plan.cost }}</v-col>
                   <v-col cols="6"><strong>Início:</strong> {{ plan.startDate }}</v-col>
                   <v-col cols="6"><strong>Fim:</strong> {{ plan.endDate }}</v-col>
                   <v-col cols="12"><strong>Indicadores:</strong> {{ planIndicatorsLabel(plan) }}</v-col>
               </v-row>
               <v-divider class="my-2"></v-divider>
               <div class="d-flex justify-end">
                   <v-spacer></v-spacer>
                   <v-btn text small color="primary" @click="editPlan(index)">
                       <v-icon left>mdi-pencil</v-icon> Editar
                   </v-btn>
                   <v-btn text small color="red" @click="removePlan(index)">
                       <v-icon left>mdi-delete</v-icon> Excluir
                   </v-btn>
               </div>
            </v-expansion-panel-content>
          </v-expansion-panel>
        </v-expansion-panels>

        <!-- Dialog de Novo/Editar Plano -->
        <v-dialog v-model="dialogPlan" max-width="800px" persistent>
            <v-card>
                <v-card-title class="indigo white--text">
                    {{ editingPlanIndex > -1 ? 'Editar Plano' : 'Novo Plano' }}
                </v-card-title>
                <v-card-text class="pt-4">
                    <v-row dense>
                        <v-col cols="12" md="6">
                            <v-textarea v-model="newPlan.what" label="O Quê" outlined rows="3" dense hide-details="auto"></v-textarea>
                        </v-col>
                        <v-col cols="12" md="6">
                            <v-textarea v-model="newPlan.how" label="Como" outlined rows="3" dense hide-details="auto"></v-textarea>
                        </v-col>
                        <v-col cols="12" md="6">
                            <v-text-field v-model="newPlan.where" label="Onde" outlined dense hide-details="auto"></v-text-field>
                        </v-col>
                        <v-col cols="12" md="6">
                            <v-text-field v-model="newPlan.who" label="Quem" outlined dense hide-details="auto"></v-text-field>
                        </v-col>
                        <v-col cols="12" md="6">
                            <v-text-field v-model="newPlan.cost" label="Quanto vai custar" outlined dense hide-details="auto"></v-text-field>
                        </v-col>
                        <v-col cols="12" md="6">
                            <v-text-field v-model="newPlan.responsible" label="Responsável" outlined dense hide-details="auto"></v-text-field>
                        </v-col>
                        <v-col cols="12" md="6">
                            <v-menu v-model="menuStart" :close-on-content-click="false" transition="scale-transition" offset-y min-width="auto">
                                <template v-slot:activator="{ on, attrs }">
                                    <v-text-field v-model="newPlan.startDate" label="Data Início" prepend-icon="mdi-calendar" readonly v-bind="attrs" v-on="on" dense outlined hide-details="auto"></v-text-field>
                                </template>
                                <v-date-picker v-model="newPlan.startDate" @input="menuStart = false"></v-date-picker>
                            </v-menu>
                        </v-col>
                        <v-col cols="12" md="6">
                            <v-menu v-model="menuEnd" :close-on-content-click="false" transition="scale-transition" offset-y min-width="auto">
                                <template v-slot:activator="{ on, attrs }">
                                    <v-text-field v-model="newPlan.endDate" label="Data Fim" prepend-icon="mdi-calendar" readonly v-bind="attrs" v-on="on" dense outlined hide-details="auto"></v-text-field>
                                </template>
                                <v-date-picker v-model="newPlan.endDate" @input="menuEnd = false"></v-date-picker>
                            </v-menu>
                        </v-col>
                        <v-col cols="12" md="12">
                            <v-autocomplete
                              v-model="selectedIndicatorIds"
                              :items="indicatorSelectItems"
                              item-text="displayName"
                              item-value="optionKey"
                              label="Indicadores"
                              multiple
                              chips
                              small-chips
                              deletable-chips
                              outlined
                              dense
                              hide-details="auto"
                              :loading="indicators.loading"
                              :no-data-text="indicatorNoDataText"
                            >
                              <template v-slot:item="{ item, on, attrs }">
                                <v-list-item v-bind="attrs" v-on="on">
                                  <v-list-item-action>
                                    <v-checkbox :input-value="attrs.inputValue"></v-checkbox>
                                  </v-list-item-action>
                                  <v-list-item-content>
                                    <v-list-item-title>{{ item.code }} - {{ item.name }}</v-list-item-title>
                                    <v-list-item-subtitle v-if="item.sectorName">{{ item.sectorName }}</v-list-item-subtitle>
                                  </v-list-item-content>
                                </v-list-item>
                              </template>
                            </v-autocomplete>
                            <div class="caption grey--text text--darken-1 mt-1">
                              <template v-if="indicators.error">
                                <span class="red--text">Não foi possível carregar os indicadores.</span>
                                <v-btn text x-small color="primary" @click="reloadIndicators">Tentar novamente</v-btn>
                              </template>
                              <template v-else-if="indicatorPerspectiveIds.length > 0">
                                {{ indicators.options.length }} indicador(es) operacional(is)<span v-if="indicators.hospitalUnitName"> de {{ indicators.hospitalUnitName }}</span>
                                · perspectivas: {{ indicatorPerspectiveNames }}
                              </template>
                            </div>
                        </v-col>
                        <v-col cols="12" class="mt-2" md="4">
                            <v-select v-model="newPlan.status" :items="planStatusOptions" label="Status do Plano" outlined dense hide-details="auto"></v-select>
                        </v-col>
                    </v-row>
                </v-card-text>
                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn text color="grey" @click="dialogPlan = false">Cancelar</v-btn>
                    <v-btn color="indigo" dark @click="savePlan">Salvar Plano</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <v-divider class="my-4"></v-divider>
        <!-- Status Geral -->
        <v-row class="mt-2">
            <v-col cols="12">
                <div class="text-subtitle-2 font-weight-bold mb-2">Status da Iniciativa</div>
                <v-chip-group v-model="form.status" mandatory column>
                    <v-chip
                        v-for="status in statusOptions"
                        :key="status"
                        :value="status"
                        :color="getStatusColor(status)"
                        :outlined="form.status !== status"
                        :dark="form.status === status"
                        filter
                    >{{ status }}</v-chip>
                </v-chip-group>
            </v-col>
        </v-row>

      </v-form>
    </v-card-text>
    <v-card-actions>
      <v-btn v-if="isEditing" text color="red" @click="$emit('delete', form.id)">
        <v-icon left>mdi-delete</v-icon>Excluir
      </v-btn>
      <v-spacer></v-spacer>
      <v-btn text color="grey" @click="$emit('close')">Cancelar</v-btn>
      <v-btn color="purple" dark @click="save">Salvar</v-btn>
    </v-card-actions>
  </v-card>
</template>

<script>
import axios from 'axios';
import swotService from '@/services/swotService';
import kpiServices from '@/services/kpiServices';
import translateService from '@/helper/translateService.js';

// Status de plano de acao que conta como concluido no progresso da iniciativa
const PLAN_STATUS_DONE = 'Finalizado';

export default {
  props: {
    actionData: {
      type: Object,
      default: () => null
    },
    sector: {
      type: Object,
      required: true
    },
    preSelectedFactors: {
      type: Object,
      default: () => null
    },
    availableFactors: {
      type: Object,
      default: () => ({ strengths: [], weaknesses: [], opportunities: [], threats: [] })
    }
  },
  data() {
    return {
      valid: true,
      menuStart: false,
      menuEnd: false,
      dialogPlan: false,
      editingPlanIndex: -1,
      targetCommaWarning: false,
      resultCommaWarning: false,
      targetCommaWarningTimeout: null,
      resultCommaWarningTimeout: null,
      form: {
        id: null,
        objectiveId: null,
        goal: '',
        method: 'Qualitativo',
        targetValue: null,
        resultValue: null,
        plans: [],
        status: 'Planejado'
      },
      newPlan: {
        what: '',
        where: '',
        how: '',
        who: null,
        responsible: '',
        cost: null,
        startDate: null,
        endDate: null,
        indicators: [],
        status: 'Em andamento'
      },
      currentFactorType: null,
      currentFactorValues: [],
      confirmedFactors: [],
      typeOptions: ['Forças', 'Oportunidades', 'Fraquezas', 'Ameaças'],
      typeMap: {
        'Forças': 'strengths',
        'Fraquezas': 'weaknesses',
        'Oportunidades': 'opportunities',
        'Ameaças': 'threats',
      },
      objectiveOptions: [],
      indicators: {
        loading: false,
        error: false,
        options: [],
        hospitalUnitName: '',
        // Escopo ja carregado (setor + perspectivas), evita requisicao redundante.
        loadedKey: null
      },
      // optionKey dos indicadores marcados no dialogo de plano.
      selectedIndicatorIds: [],
      // Indicadores ja gravados no plano em edicao: preservados mesmo que saiam do escopo atual.
      editingPlanIndicators: [],
      indicatorCancelToken: null,
      statusOptions: ['Planejado', 'Em andamento', 'Concluído', 'Cancelado'],
      planStatusOptions: ['Em andamento', 'Não executado', PLAN_STATUS_DONE],
      methodOptions: ['Qualitativo', 'Quantitativo']
    };
  },
  computed: {
    totalSelectedFactors() {
      return this.confirmedFactors.length;
    },
    finishedPlansCount() {
      return this.form.plans.filter(p => p.status === PLAN_STATUS_DONE).length;
    },
    // Progresso da iniciativa: qualitativo = planos de ação finalizados / total,
    // quantitativo = resultado / meta
    completionPercentage() {
      if (this.form.method === 'Quantitativo') {
        const target = Number(this.form.targetValue);
        const result = Number(this.form.resultValue);
        if (!target || Number.isNaN(target) || target <= 0 || Number.isNaN(result)) return 0;
        return Math.max(0, Math.min(100, Math.round((result / target) * 100)));
      }
      const total = this.form.plans.length;
      if (!total) return 0;
      return Math.round((this.finishedPlansCount / total) * 100);
    },
    isEditing() {
      return !!this.form.id;
    },
    selectedObjective() {
      return this.objectiveOptions.find(o => o.id === this.form.objectiveId) || null;
    },
    /** Perspectivas distintas dos fatores SWOT confirmados. */
    factorPerspectiveIds() {
      const ids = this.confirmedFactors
        .map(f => f.perspectiveId)
        .filter(id => id !== null && id !== undefined);
      return [...new Set(ids)].sort((a, b) => a - b);
    },
    objectivePerspectiveIds() {
      return this.selectedObjective ? (this.selectedObjective.perspectiveIds || []) : [];
    },
    /**
     * Perspectivas que filtram os indicadores. Sao as dos fatores confirmados;
     * fatores cadastrados sem perspectiva caem nas do objetivo, para o campo nao
     * ficar vazio sem explicacao.
     */
    indicatorPerspectiveIds() {
      return this.factorPerspectiveIds.length > 0
        ? this.factorPerspectiveIds
        : this.objectivePerspectiveIds;
    },
    indicatorPerspectiveNames() {
      return this.indicatorPerspectiveIds
        .map(id => this.perspectiveName(id))
        .filter(Boolean)
        .join(', ');
    },
    /**
     * Opcoes do autocomplete: as carregadas da unidade mais os indicadores ja
     * gravados no plano que nao estao mais no escopo (perspectivas mudaram,
     * indicador inativado). Sem isso, reeditar um plano apagaria a selecao.
     */
    indicatorSelectItems() {
      const known = new Set(this.indicators.options.map(o => o.optionKey));
      const orphans = this.editingPlanIndicators
        .map(i => this.toIndicatorOption(i))
        .filter(o => !known.has(o.optionKey));
      return [...this.indicators.options, ...orphans];
    },
    indicatorNoDataText() {
      if (this.indicators.loading) return 'Carregando indicadores...';
      if (this.indicators.error) return 'Erro ao carregar os indicadores.';
      if (!this.sector || !this.sector.id) return 'Selecione um setor.';
      if (this.confirmedFactors.length === 0) return 'Confirme ao menos um fator SWOT para listar os indicadores.';
      // Fator sem perspectiva nao gera busca: avisar onde corrigir, em vez de
      // deixar o campo vazio sugerindo que faltou selecionar fator.
      if (this.indicatorPerspectiveIds.length === 0) return 'Os fatores selecionados não têm perspectiva cadastrada. Edite o fator e informe a perspectiva.';
      return 'Nenhum indicador operacional nesta unidade para as perspectivas dos fatores.';
    },
    // Texto editável dos campos Meta/Resultado: mantém o "." digitado durante
    // a digitação (v-model.number descartaria o ponto decimal a cada tecla).
    targetValueText: {
      get() {
        return this.form.targetValue != null ? String(this.form.targetValue) : '';
      },
      set(value) {
        this.form.targetValue = this.sanitizeDecimalInput(value);
      }
    },
    resultValueText: {
      get() {
        return this.form.resultValue != null ? String(this.form.resultValue) : '';
      },
      set(value) {
        this.form.resultValue = this.sanitizeDecimalInput(value);
      }
    }
  },
  watch: {
    // A lista de indicadores acompanha as perspectivas dos fatores confirmados.
    indicatorPerspectiveIds: {
      immediate: true,
      handler() { this.loadIndicatorOptions(); }
    }
  },
  mounted() {
    this.loadObjectives();
    if (this.actionData) {
      this.form = {
        id: this.actionData.id,
        objectiveId: this.actionData.objectiveId || null,
        goal: this.actionData.goal || '',
        method: this.actionData.method || 'Qualitativo',
        targetValue: this.actionData.targetValue != null ? this.actionData.targetValue : null,
        resultValue: this.actionData.resultValue != null ? this.actionData.resultValue : null,
        status: this.actionData.status || 'Planejado',
        plans: (this.actionData.plans || []).map(p => ({ ...p, indicators: p.indicators || [] }))
      };
      this.confirmedFactors = (this.actionData.confirmedFactors || [])
        .map(cf => ({ type: cf.type, id: cf.id, label: cf.label, perspectiveId: cf.perspectiveId }));
    }
    if (this.preSelectedFactors) {
      const typeMap = { strengths: 'Forças', weaknesses: 'Fraquezas', opportunities: 'Oportunidades', threats: 'Ameaças' };
      for (const [key, items] of Object.entries(this.preSelectedFactors)) {
        if (items && items.length > 0) {
          const type = typeMap[key];
          items.forEach(i => {
            this.confirmedFactors.push({
              type,
              id: i.id,
              label: i.displayDescription || i.id,
              perspectiveId: i.perspectiveId
            });
          });
        }
      }
    }
  },
  beforeDestroy() {
    this.cancelIndicatorRequest();
    clearTimeout(this.targetCommaWarningTimeout);
    clearTimeout(this.resultCommaWarningTimeout);
  },
  methods: {
    // Bloqueia no teclado qualquer tecla que não seja dígito ou o primeiro ".",
    // para que Meta/Resultado só aceitem números com separador decimal por ponto.
    // Ao digitar vírgula, avisa o usuário em vez de só ignorar a tecla.
    blockInvalidDecimalKey(event, field) {
      const navigationKeys = ['Backspace', 'Delete', 'Tab', 'Escape', 'Enter', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End'];
      if (navigationKeys.includes(event.key) || event.ctrlKey || event.metaKey) return;
      if (event.key === ',') {
        event.preventDefault();
        this.showCommaWarning(field);
        return;
      }
      const isDigit = /^[0-9]$/.test(event.key);
      const isFirstDot = event.key === '.' && !event.target.value.includes('.');
      if (!isDigit && !isFirstDot) {
        event.preventDefault();
      }
    },
    showCommaWarning(field) {
      const warningKey = field === 'target' ? 'targetCommaWarning' : 'resultCommaWarning';
      const timeoutKey = field === 'target' ? 'targetCommaWarningTimeout' : 'resultCommaWarningTimeout';
      this[warningKey] = true;
      if (this[timeoutKey]) {
        clearTimeout(this[timeoutKey]);
      }
      this[timeoutKey] = setTimeout(() => {
        this[warningKey] = false;
      }, 3000);
    },
    // Segunda barreira para valores colados: troca vírgula por ponto e remove
    // qualquer caractere que não seja dígito, mantendo só o primeiro ponto.
    sanitizeDecimalInput(value) {
      if (value == null) return null;
      let sanitized = String(value).replace(',', '.').replace(/[^0-9.]/g, '');
      const firstDot = sanitized.indexOf('.');
      if (firstDot !== -1) {
        sanitized = sanitized.slice(0, firstDot + 1) + sanitized.slice(firstDot + 1).replace(/\./g, '');
      }
      return sanitized === '' ? null : sanitized;
    },
    perspectiveName(perspectiveId) {
      const p = (this.$store.state.current.perspectives || []).find(x => x.id === perspectiveId);
      return p ? translateService.getName(p.names) : '';
    },
    perspectiveIcon(perspectiveId) {
      const p = (this.$store.state.current.perspectives || []).find(x => x.id === perspectiveId);
      return p && p.icon ? p.icon : 'mdi-archive';
    },
    perspectiveColor(perspectiveId) {
      const p = (this.$store.state.current.perspectives || []).find(x => x.id === perspectiveId);
      return p && p.color ? p.color : 'grey';
    },
    objectivePerspectiveNames(objective) {
      return (objective.perspectiveIds || [])
        .map(pid => this.perspectiveName(pid))
        .filter(Boolean)
        .join(', ');
    },
    loadObjectives() {
      const stageId = this.$store.state.current.stage && this.$store.state.current.stage.id;
      if (!stageId) return;
      // Objetivos de todos os setores do ciclo, não apenas do setor da iniciativa
      swotService.getAllObjectives(stageId)
        .then(resp => {
          this.objectiveOptions = (resp.data || []).map(o => ({
            id: o.id,
            code: o.code,
            description: o.description,
            label: `${o.code} - ${o.description}`,
            perspectiveIds: o.perspectiveIds || []
          }));
        })
        .catch(() => { this.objectiveOptions = []; });
    },
    /**
     * Normaliza um indicador (opcao vinda da API ou registro ja gravado no plano)
     * para o formato do autocomplete. optionKey usa o kpiId quando existe e cai no
     * kpiTemplateId nos registros anteriores a V6, que so referenciam o modelo.
     */
    toIndicatorOption(indicator) {
      const kpiId = indicator.kpiId === undefined ? null : indicator.kpiId;
      const kpiTemplateId = indicator.kpiTemplateId === undefined ? null : indicator.kpiTemplateId;
      const code = indicator.code || '';
      const name = indicator.name || code;
      const sectorName = indicator.sectorName || '';
      return {
        optionKey: kpiId !== null ? 'k' + kpiId : 't' + kpiTemplateId,
        kpiId,
        kpiTemplateId,
        code,
        name,
        sectorId: indicator.sectorId === undefined ? null : indicator.sectorId,
        sectorName,
        displayName: [code, name].filter(Boolean).join(' - ') + (sectorName ? ' · ' + sectorName : '')
      };
    },
    cancelIndicatorRequest() {
      if (this.indicatorCancelToken) {
        this.indicatorCancelToken.cancel('Escopo de indicadores alterado');
        this.indicatorCancelToken = null;
      }
    },
    reloadIndicators() {
      this.indicators.loadedKey = null;
      this.loadIndicatorOptions();
    },
    /**
     * Busca os indicadores operacionais da unidade hospitalar do setor da iniciativa,
     * restritos as perspectivas dos fatores confirmados. Unidade e nivel operacional
     * sao resolvidos no backend a partir de core.sector.hospital_unit_id.
     */
    loadIndicatorOptions() {
      const sectorId = this.sector && this.sector.id;
      const perspectiveIds = this.indicatorPerspectiveIds;

      if (!sectorId || perspectiveIds.length === 0) {
        this.cancelIndicatorRequest();
        this.indicators.options = [];
        this.indicators.hospitalUnitName = '';
        this.indicators.error = false;
        this.indicators.loading = false;
        this.indicators.loadedKey = null;
        return;
      }

      const key = sectorId + ':' + perspectiveIds.join(',');
      if (key === this.indicators.loadedKey) return;

      this.cancelIndicatorRequest();
      const source = axios.CancelToken.source();
      this.indicatorCancelToken = source;
      this.indicators.loading = true;
      this.indicators.error = false;

      kpiServices.getOperationalByPerspectives(sectorId, perspectiveIds, source)
        .then(resp => {
          if (this.indicatorCancelToken !== source) return;
          const data = resp.data || [];
          this.indicators.options = data.map(o => this.toIndicatorOption({
            kpiId: o.id,
            kpiTemplateId: o.templateId,
            code: o.code,
            name: o.name,
            sectorId: o.sectorId,
            sectorName: o.sectorName
          }));
          this.indicators.hospitalUnitName = data.length > 0 ? (data[0].hospitalUnitName || '') : '';
          this.indicators.loadedKey = key;
          this.indicators.loading = false;
          this.indicatorCancelToken = null;
        })
        .catch(err => {
          // Requisicao substituida por outra mais recente: nao e erro.
          if (axios.isCancel(err) || this.indicatorCancelToken !== source) return;
          this.indicators.options = [];
          this.indicators.hospitalUnitName = '';
          this.indicators.error = true;
          this.indicators.loading = false;
          this.indicators.loadedKey = null;
          this.indicatorCancelToken = null;
          if (this.$toastr) this.$toastr.e('Erro ao carregar os indicadores da unidade hospitalar.');
        });
    },
    openPlanForm() {
        this.editingPlanIndex = -1;
        this.editingPlanIndicators = [];
        this.selectedIndicatorIds = [];
        this.newPlan = {
            what: '',
            where: '',
            how: '',
            who: null,
            responsible: '',
            cost: null,
            startDate: null,
            endDate: null,
            indicators: [],
            status: 'Em andamento'
        };
        this.dialogPlan = true;
    },
    editPlan(index) {
        this.editingPlanIndex = index;
        const plan = JSON.parse(JSON.stringify(this.form.plans[index]));
        // O autocomplete trabalha com chaves; os objetos ficam guardados para
        // serem reconstruidos no salvamento, inclusive os fora do escopo atual.
        this.editingPlanIndicators = plan.indicators || [];
        this.selectedIndicatorIds = this.editingPlanIndicators.map(i => this.toIndicatorOption(i).optionKey);
        this.newPlan = { ...plan, indicators: [] };
        this.dialogPlan = true;
    },
    /** Converte as chaves marcadas de volta em indicadores gravaveis. */
    buildSelectedIndicators() {
      const byKey = new Map(this.indicatorSelectItems.map(o => [o.optionKey, o]));
      return this.selectedIndicatorIds
        .map(key => byKey.get(key))
        .filter(Boolean)
        .map(o => ({
          kpiId: o.kpiId,
          kpiTemplateId: o.kpiTemplateId,
          code: o.code,
          name: o.name,
          sectorId: o.sectorId,
          sectorName: o.sectorName
        }));
    },
    planIndicatorsLabel(plan) {
      const items = (plan && plan.indicators) || [];
      if (items.length === 0) return '—';
      return items
        .map(i => {
          const base = [i.code, i.name].filter(Boolean).join(' - ');
          return i.sectorName ? base + ' (' + i.sectorName + ')' : base;
        })
        .join(', ');
    },
    savePlan() {
      if (!this.newPlan.what) return;

      const plan = { ...this.newPlan, indicators: this.buildSelectedIndicators() };
      if (this.editingPlanIndex > -1) {
          this.$set(this.form.plans, this.editingPlanIndex, plan);
      } else {
          this.form.plans.push({ ...plan, id: Date.now() });
      }

      this.dialogPlan = false;
    },
    removePlan(index) {
      this.form.plans.splice(index, 1);
    },
    getFactorOptions(type) {
      const key = this.typeMap[type];
      return (this.availableFactors && this.availableFactors[key]) || [];
    },
    /** Ao selecionar um fator no combo, já insere na lista de fatores adicionados. */
    onFactorValuesChange(values) {
      const type = this.currentFactorType;
      if (!type || !values || values.length === 0) return;
      values.forEach(id => {
        const alreadyAdded = this.confirmedFactors.some(f => f.type === type && f.id === id);
        if (!alreadyAdded) {
          const factor = this.findFactor(type, id);
          this.confirmedFactors.push({
            type,
            id,
            label: factor ? factor.displayDescription : id,
            perspectiveId: factor ? factor.perspectiveId : null
          });
        }
      });
      this.currentFactorValues = [];
    },
    removeConfirmedFactor(index) {
      this.confirmedFactors.splice(index, 1);
    },
    getTypeColor(type) {
      const colors = { 'Forças': 'green darken-1', 'Fraquezas': 'orange darken-1', 'Oportunidades': 'blue darken-1', 'Ameaças': 'red darken-1' };
      return colors[type] || 'grey';
    },
    findFactor(type, id) {
      const key = this.typeMap[type];
      const items = (this.availableFactors && this.availableFactors[key]) || [];
      return items.find(i => i.id === id) || null;
    },
    getFactorLabel(type, id) {
      const factor = this.findFactor(type, id);
      return factor ? factor.displayDescription : id;
    },
    save() {
      this.$emit('save', {
        id: this.form.id,
        objectiveId: this.form.objectiveId,
        goal: this.form.goal,
        method: this.form.method,
        targetValue: this.form.method === 'Quantitativo' && this.form.targetValue != null ? Number(this.form.targetValue) : null,
        resultValue: this.form.method === 'Quantitativo' && this.form.resultValue != null ? Number(this.form.resultValue) : null,
        completionPercentage: this.completionPercentage,
        status: this.form.status,
        confirmedFactors: this.confirmedFactors,
        plans: this.form.plans
      });
    },
    getStatusColor(status) {
        switch(status) {
            case 'Concluído':
            case 'Finalizado': return 'green';
            case 'Em andamento': return 'blue';
            case 'Cancelado':
            case 'Não executado': return 'red';
            default: return 'grey';
        }
    }
  }
};
</script>

<style scoped>
</style>
